import { useEffect, useRef } from 'react';

const CLOSE_BUTTON_TESTID = 'inbox-header-close-button';

// Mobile z-index sits below mini games (3) when closed, above site header (4) when open.
const Z = {
    desktop: '11',
    mobileOpen: '6',
    mobileClosed: '1',
} as const;

export const OPTIMOVE_HOST_OPEN_EVENT = 'optimove-host-open';

export const setOptimoveHostOpen = (id: string): void => {
    const host = document.getElementById(id);
    if (host === null) return;

    host.setAttribute('data-open', '');
    host.style.visibility = 'visible';
    host.style.pointerEvents = 'auto';
    host.dispatchEvent(new CustomEvent(OPTIMOVE_HOST_OPEN_EVENT));
    if (window.matchMedia('screen and (min-width: 1024px)').matches === false) {
        host.style.zIndex = Z.mobileOpen;
    }
};

// Mobile bottom offset. With chat launchers the badge is stacked clear of the launcher.
const MOBILE_BOTTOM = {
    sports: {
        withIntercom: '161px',
        withFreshChat: '145px',
        withoutChat: '115px',
    },
    casino: {
        withIntercom: '126px',
        withFreshChat: '105px',
        withoutChat: '70px',
    },
} as const;

const DESKTOP_RIGHT = {
    withIntercom: '85px',
    withFreshChat: '145px',
    withoutChat: '25px',
} as const;

interface HostPosition {
    zIndex: string;
    bottom: string;
    right: string;
}

interface OptimoveManualStyleConfig {
    footerColor: string;
    footerBodyColor: string;
    inlineLinkColor: string;
}

const getHostPosition = (
    isDesktop: boolean,
    isCasino: boolean,
    hasIntercom: boolean,
    hasFreshChat: boolean,
    isOpen: boolean
): HostPosition => {
    if (isDesktop) {
        return {
            zIndex: Z.desktop,
            bottom: '28px',
            right: hasIntercom
                ? DESKTOP_RIGHT.withIntercom
                : hasFreshChat
                  ? DESKTOP_RIGHT.withFreshChat
                  : DESKTOP_RIGHT.withoutChat,
        };
    }

    const layout = isCasino ? MOBILE_BOTTOM.casino : MOBILE_BOTTOM.sports;
    return {
        zIndex: isOpen ? Z.mobileOpen : Z.mobileClosed,
        bottom: hasIntercom ? layout.withIntercom : hasFreshChat ? layout.withFreshChat : layout.withoutChat,
        right: hasIntercom ? '32px' : '25px',
    };
};

/**
 * Builds application-owned overrides for Optimove's open Shadow DOM.
 * These selectors cover inbox elements without corresponding Optimove CSS variables;
 * supported Optimove styling remains in optimoveInbox.style.tsx.
 */
const addManualInboxStyles = (
    footerColor: string,
    footerBodyColor: string,
    inlineLinkColor: string
): HTMLStyleElement => {
    const style = document.createElement('style');
    style.textContent = `
        .optimobile-message-inbox {
            overscroll-behavior: contain;
        }

        ${
            footerColor === ''
                ? ''
                : `.optimobile-inbox-message-custom-footer-link,
        .optimobile-inbox-message-custom-footer-link:visited,
        .optimobile-inbox-message-custom-footer-expandable-toggle {
            color: ${footerColor} !important;
        }`
        }
        ${
            footerBodyColor === ''
                ? ''
                : `.optimobile-inbox-message-custom-footer-body {
            color: ${footerBodyColor} !important;
                }`
        }
        ${
            inlineLinkColor === ''
                ? ''
                : `.optimobile-inbox-inline-link,
        .optimobile-inbox-inline-link:visited {
            color: ${inlineLinkColor} !important;
        }`
        }
    `;
    return style;
};

/**
 * Positions the Optimove badge host element and manages z-index when the inbox opens.
 *
 * The SDK uses an open shadow DOM, so composedPath() lets us distinguish badge clicks (open)
 * from close button clicks. On mobile the host sits below mini games (z-index 1) until the
 * inbox opens, then rises above the site header (z-index 6). On desktop z-index 11 always.
 *
 * Two effects are intentional: the click handler must not re-run on breakpoint/casino changes
 * or it would reset open state mid-session.
 */
export const useOptimoveHost = (
    id: string | null,
    isDesktop: boolean,
    isCasino: boolean,
    hasIntercom: boolean,
    hasFreshChat: boolean,
    isHidden: boolean,
    hideWhenClosed: boolean,
    manualStyleConfig: OptimoveManualStyleConfig
): void => {
    // Refs let the stable click-handler effect read current values without being in its deps.
    const isDesktopRef = useRef(isDesktop);
    isDesktopRef.current = isDesktop;

    const isOpenRef = useRef(false);
    const hideWhenClosedRef = useRef(hideWhenClosed);
    hideWhenClosedRef.current = hideWhenClosed;
    const { footerColor, footerBodyColor, inlineLinkColor } = manualStyleConfig;

    // Re-runs on layout changes to update position. Skips z-index reset if inbox is open.
    useEffect(() => {
        if (id === null) return;
        const host = document.getElementById(id);
        if (host === null) return;

        if (isHidden) {
            isOpenRef.current = false;
            host.removeAttribute('data-open');
            host.style.display = 'none';
            return;
        }

        host.style.display = '';
        host.style.visibility = hideWhenClosed && !isOpenRef.current ? 'hidden' : 'visible';
        host.style.pointerEvents = hideWhenClosed && !isOpenRef.current ? 'none' : 'auto';
        host.style.position = 'fixed';
        host.style.left = 'auto';
        host.style.top = 'auto';
        host.style.overflow = 'visible';
        host.style.width = '48px';
        host.style.height = '48px';

        const { zIndex, bottom, right } = getHostPosition(
            isDesktop,
            isCasino,
            hasIntercom,
            hasFreshChat,
            isOpenRef.current
        );
        host.style.zIndex = zIndex;
        host.style.bottom = bottom;
        host.style.right = right;
    }, [isDesktop, isCasino, hasIntercom, hasFreshChat, id, isHidden, hideWhenClosed]);

    // Stable — only re-runs if the host element itself changes.
    useEffect(() => {
        if (id === null) return;
        if (isHidden) return;
        const host = document.getElementById(id);
        if (host === null) return;

        const openHost = (): void => {
            isOpenRef.current = true;
            host.setAttribute('data-open', '');
            host.style.visibility = 'visible';
            host.style.pointerEvents = 'auto';
            if (!isDesktopRef.current) host.style.zIndex = Z.mobileOpen;
        };

        // Shadow DOM is open — composedPath() includes shadow elements, so we can detect
        // the close button click directly rather than inferring state from outside clicks.
        const handleHostClick = (e: MouseEvent): void => {
            const isCloseButton = e
                .composedPath()
                .some((el) => el instanceof Element && el.getAttribute('data-testid') === CLOSE_BUTTON_TESTID);
            if (isCloseButton) {
                isOpenRef.current = false;
                host.removeAttribute('data-open');
                if (!isDesktopRef.current) {
                    host.style.zIndex = Z.mobileClosed;
                    if (hideWhenClosedRef.current) {
                        host.style.visibility = 'hidden';
                        host.style.pointerEvents = 'none';
                    }
                }
            } else if (!isOpenRef.current) {
                openHost();
            }
        };

        host.addEventListener('click', handleHostClick);
        host.addEventListener(OPTIMOVE_HOST_OPEN_EVENT, openHost);

        const shadow = host.querySelector('#optimobile-inbox-ui-root-inbox-container')?.shadowRoot ?? null;
        let manualStyles: HTMLStyleElement | null = null;
        if (shadow !== null) {
            manualStyles = addManualInboxStyles(footerColor, footerBodyColor, inlineLinkColor);
            shadow.appendChild(manualStyles);
        }

        return () => {
            host.removeEventListener('click', handleHostClick);
            host.removeEventListener(OPTIMOVE_HOST_OPEN_EVENT, openHost);
            manualStyles?.remove();
        };
    }, [id, isHidden, footerBodyColor, footerColor, inlineLinkColor]);
};
